import { useInView } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export function useScrollable(shift = 300, stopScroll = false, isLoading = false) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const isInView = useInView(containerRef, { margin: '200px' })

  useEffect(() => {
    if (stopScroll) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
    }
  }, [stopScroll])

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -shift, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: shift, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const container = containerRef.current
    // Only read scrollWidth while the rail is on/near screen (isInView, via useInView). The rails set
    // content-visibility:auto (Swimlane), so reading scrollWidth on an off-screen rail forces the browser
    // to compute its skipped layout — a large, needless reflow during load. Gating the whole effect on
    // isInView means layout work only happens for rails the user can actually see (the arrows are a hover
    // affordance anyway).
    if (!container || isLoading || !isInView) return

    // Structure-agnostic: derive the affordance from the container's own scroll geometry, so it is
    // correct for every rail shape — direct card children, a single flex wrapper, or trailing arrow
    // overlays alike. The expensive width read is split from the per-scroll read: scrollWidth /
    // clientWidth are read only when they can change (ResizeObserver, where the read is reflow-safe,
    // and card streaming) and cached as maxScroll; scroll then reads only scrollLeft, which does not
    // force layout. This avoids the per-scroll scrollWidth read the old code did on every event.
    let maxScroll = 0

    const update = () => {
      setCanScrollLeft(container.scrollLeft > 0)
      setCanScrollRight(container.scrollLeft < maxScroll - 1)
    }

    const measure = () => {
      maxScroll = container.scrollWidth - container.clientWidth
      update()
    }

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(container)

    // subtree so cards streamed into an inner wrapper (e.g. paginated profile rails) re-measure.
    const mutationObserver = new MutationObserver(measure)
    mutationObserver.observe(container, { childList: true, subtree: true })

    container.addEventListener('scroll', update, { passive: true })
    measure()

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      container.removeEventListener('scroll', update)
    }
  }, [isLoading, isInView])

  return {
    containerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
  }
}
